// src/pages/admin/analytics/components/RecipeVarianceTheftTab.jsx
import { ShieldAlert, AlertTriangle, Coffee, PackageCheck, AlertCircle, RefreshCw } from 'lucide-react';

const RecipeVarianceTheftTab = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <RefreshCw className="h-8 w-8 animate-spin text-orange-500 mr-3" />
        <span className="font-medium text-lg">Analyzing Recipe Consumption & Theft Discrepancies...</span>
      </div>
    );
  }

  const summary = data?.summary || {};
  const variances = data?.variances || [];
  const highRiskAlerts = variances.filter((v) => v.riskLevel === 'HIGH_RISK_THEFT');

  return (
    <div className="space-y-6">
      {/* Theft Alert Banner */}
      {highRiskAlerts.length > 0 ? (
        <div className="p-5 bg-gradient-to-r from-amber-600 via-orange-600 to-red-700 text-white rounded-xl shadow-lg border border-orange-500 flex items-start space-x-4">
          <div className="p-3 bg-white/20 rounded-lg">
            <ShieldAlert className="h-8 w-8 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black tracking-wide">
                THEFT & LEAKAGE ALERT: {highRiskAlerts.length} High Discrepancy Ingredient(s)
              </h3>
              <span className="px-3 py-1 bg-white text-orange-800 font-bold rounded-full text-xs uppercase tracking-wider">
                Auditing Required
              </span>
            </div>
            <p className="text-sm text-amber-100 mt-1">
              Theoretical recipe consumption + recorded wastage does not match expected inventory depletion. High shortage risk detected in key raw materials.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {highRiskAlerts.map((ha) => (
                <span key={ha.rawMaterialId} className="px-3 py-1 bg-white/20 text-white rounded-md text-xs font-semibold">
                  ⚠️ {ha.name}: Expected Usage {ha.totalExpectedUsage} {ha.unitName} (Estimated Shortage: Rs. {ha.estimatedShortageCost.toFixed(2)})
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl flex items-center space-x-3">
          <PackageCheck className="h-6 w-6 text-emerald-600 flex-shrink-0" />
          <div>
            <h4 className="font-bold text-sm">No Critical Inventory Discrepancies</h4>
            <p className="text-xs text-emerald-700">Theoretical recipe usage aligns well with logged wastage and inventory depletion levels.</p>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase">Tracked Raw Materials</p>
          <p className="text-2xl font-black text-gray-900 mt-1">{summary.trackedMaterialsCount || variances.length}</p>
          <p className="text-xs text-gray-500 mt-1">Active recipe ingredients</p>
        </div>

        <div className="bg-white rounded-xl p-5 border border-amber-200 bg-amber-50/20 shadow-sm">
          <p className="text-xs font-semibold text-amber-700 uppercase">Estimated Shortage Cost</p>
          <p className="text-2xl font-black text-amber-800 mt-1">Rs. {(summary.totalShortageCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
          <p className="text-xs text-amber-600 mt-1">Unaccounted material loss</p>
        </div>

        <div className="bg-white rounded-xl p-5 border border-rose-200 bg-rose-50/20 shadow-sm">
          <p className="text-xs font-semibold text-rose-700 uppercase">High Risk Theft Alerts</p>
          <p className="text-2xl font-black text-rose-800 mt-1">{summary.highRiskAlerts || highRiskAlerts.length}</p>
          <p className="text-xs text-rose-600 mt-1">Requires immediate physical audit</p>
        </div>
      </div>

      {/* Recipe Consumption vs Inventory Variance Table */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900 text-sm flex items-center">
              <Coffee className="h-4 w-4 text-orange-500 mr-2" />
              Raw Material Recipe Consumption vs Inventory Audit
            </h3>
            <p className="text-xs text-gray-500">Compares theoretical sales recipe usage against logged wastage and current stock.</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-gray-100 border-b border-gray-200 text-gray-600 font-semibold uppercase tracking-wider">
              <tr>
                <th className="p-4">Raw Material</th>
                <th className="p-4 text-right">Unit Price</th>
                <th className="p-4 text-center">Theoretical Usage (Sales x Recipe)</th>
                <th className="p-4 text-center">Manual Logged Wastage</th>
                <th className="p-4 text-center">Total Expected Depletion</th>
                <th className="p-4 text-center">Current Stock</th>
                <th className="p-4 text-right">Shortage Cost (LKR)</th>
                <th className="p-4 text-center">Risk Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-medium">
              {variances.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-gray-500">
                    No recipe usage data recorded for the selected period.
                  </td>
                </tr>
              ) : (
                variances.map((v) => {
                  const isHighRisk = v.riskLevel === 'HIGH_RISK_THEFT';
                  const isModerate = v.riskLevel === 'MODERATE_VARIANCE';

                  return (
                    <tr
                      key={v.rawMaterialId}
                      className={`hover:bg-gray-50 transition-colors ${
                        isHighRisk ? 'bg-rose-50/40' : isModerate ? 'bg-amber-50/30' : ''
                      }`}
                    >
                      <td className="p-4">
                        <p className="font-bold text-gray-900">{v.name}</p>
                        <p className="text-[10px] text-gray-500">Code: {v.code}</p>
                      </td>
                      <td className="p-4 text-right text-gray-600">Rs. {(v.unitPrice || 0).toFixed(2)}</td>
                      <td className="p-4 text-center font-bold text-blue-700">
                        {v.theoreticalUsage} {v.unitName}
                      </td>
                      <td className="p-4 text-center font-bold text-rose-600">
                        {v.manualWastageQty > 0 ? `${v.manualWastageQty} ${v.unitName}` : '-'}
                      </td>
                      <td className="p-4 text-center font-black text-gray-900">
                        {v.totalExpectedUsage} {v.unitName}
                      </td>
                      <td className="p-4 text-center text-slate-700">
                        {v.currentStock} {v.unitName}
                      </td>
                      <td className="p-4 text-right font-black text-amber-700">
                        Rs. {v.estimatedShortageCost.toFixed(2)}
                      </td>
                      <td className="p-4 text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold ${
                            isHighRisk
                              ? 'bg-red-600 text-white shadow-sm'
                              : isModerate
                              ? 'bg-amber-500 text-white shadow-sm'
                              : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          {isHighRisk ? '⚠️ High Theft Risk' : isModerate ? '⚡ Moderate' : '✓ Normal'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RecipeVarianceTheftTab;
